/** 建项前先核对地址：同址只能打开原项目；疑似地址须确认不同地点并说明，凭据绑定本次候选。
 * 两个建项表单共用交互，后台再次检查，避免刷新页面或并发请求绕过。
 */
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, ExternalLink } from "lucide-react";
import { useApp } from "../context/AppContext";

export function useProjectAddressCheck(api, address, city = "") {
  const [result, setResult] = useState(null);
  const [reason, setReason] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  useEffect(() => { setResult(null); setReason(""); setConfirmed(false); }, [address, city]);
  const acknowledgment = result && !result.blocked && confirmed && reason.trim().length >= 5
    ? { fingerprint: result.fingerprint, reason: reason.trim() } : null;
  const check = async (body, kind) => {
    const { data } = await api.post("/project-address-check", { ...body, kind });
    setResult(data);
    if (!data.candidates.length) return {};
    return !data.blocked && acknowledgment?.fingerprint === data.fingerprint ? acknowledgment : null;
  };
  const handleError = (e) => {
    const detail = e?.response?.data?.detailRaw ?? e?.response?.data?.detail;
    if (detail?.code !== "project_address_conflict") return false;
    setResult(detail); setConfirmed(false); return true;
  };
  return { result, reason, setReason, confirmed, setConfirmed, check, handleError };
}

export function ProjectAddressCheck({ state, onLeave }) {
  const { lang } = useApp();
  const zh = lang === "zh", nav = useNavigate();
  const r = state.result;
  const panel = useRef(null);
  // 长表单的提交按钮在底部；查重提示出现后将焦点移到提示，手机用户能立即看到原因。
  useEffect(() => {
    if (r?.candidates?.length) { panel.current?.focus(); panel.current?.scrollIntoView?.({ block: "nearest" }); }
  }, [r?.fingerprint, r?.candidates?.length]);
  if (!r?.candidates?.length) return null;
  const open = (url) => { onLeave?.(); nav(url); };
  return <section ref={panel} tabIndex={-1} role="alert" className="rounded-xl border border-amber-300 bg-amber-50 p-4 space-y-3 text-sm" data-testid="project-address-conflict">
    <h3 className="flex items-center gap-2 font-semibold text-amber-950"><AlertTriangle className="h-5 w-5 shrink-0" />{zh ? (r.blocked ? "此地址已有项目，请使用原项目" : "是否与以下项目为同一地点？") : (r.blocked ? "A project already exists at this address" : "Is this the same site as an existing project?")}</h3>
    <p className="text-amber-900">{zh ? "修改图纸请在原项目内发起设计变更，无需重新创建项目。" : "For drawing revisions, create a design change in the original project."}</p>
    {r.candidates.map((p, i) => <div key={`${p.kind}-${p.id || i}`} className="rounded-lg border border-amber-200 bg-white p-3 space-y-2">
      <p className="font-semibold text-slate-900 break-words">{p.address}</p>
      <p className="text-xs text-slate-600">{p.kind === "design" ? (zh ? "设计项目" : "Design") : (zh ? "工程项目" : "Engineering")} · {p.owner} {p.phase ? `· ${p.phase}` : ""}</p>
      {p.id && <div className="flex flex-wrap gap-2">
        <button type="button" className="min-h-11 rounded-lg border px-3 text-indigo-700 inline-flex items-center gap-1" onClick={() => open(p.kind === "design" ? `/design/projects/${p.id}` : `/project/${p.id}`)}><ExternalLink className="h-4 w-4" />{zh ? "打开原项目" : "Open project"}</button>
        {p.designProjectId && <button type="button" className="min-h-11 rounded-lg border px-3 text-indigo-700" onClick={() => open(`/design/changes/${p.designProjectId}`)}>{zh ? "设计变更" : "Design changes"}</button>}
      </div>}
    </div>)}
    {!r.blocked && <div className="space-y-2">
      <label className="flex min-h-11 items-center gap-2 text-slate-900"><input type="checkbox" checked={state.confirmed} onChange={(e) => state.setConfirmed(e.target.checked)} />{zh ? "已核对，确实是不同地点" : "I verified these are different sites"}</label>
      <label className="block text-slate-800">{zh ? "区别说明（至少 5 个字，例如城市、房号或地址写错的原因）" : "Explain the difference (at least 5 characters)"}<textarea value={state.reason} maxLength={1000} onChange={(e) => state.setReason(e.target.value)} className="mt-1 w-full rounded-lg border border-slate-300 bg-white p-2 text-base" rows={2} /></label>
      <p className="text-xs text-slate-600">{zh ? "核对后再次点击创建，系统将保存核对记录。" : "Click Create again after verification. The explanation will be recorded."}</p>
    </div>}
  </section>;
}
